import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { showSuccessToast, showErrorToast } from '@/utils/toast.utils';
import { showCreateSixModal, showEditSixModal } from '@/store/thunks/ui/uiThunk';
import { fetchSixCatalogThunk } from '@/store/thunks/sixCatalog/fetchSixCatalog';
import { setSearchAndFetchThunk } from '@/store/thunks/sixCatalog/setSearchAndFetchThunk';
import { setSortAndFetchThunk } from '@/store/thunks/sixCatalog/setSortAndFetchThunk';
import { deleteSixThunk } from '@/store/thunks/sixCatalog/deleteSixThunk';
import { toggleFavoriteSixThunk } from '@/store/thunks/sixCatalog/toggleFavoriteSixThunk';
import { selectCatalogSixes, selectCatalogSearch, selectCatalogFilters } from '@/store/selectors/sixCatalog';
import { useClientCatalogFiltering } from '@/hooks/sixCatalog/useClientCatalogFiltering';
import { selectConnectionStatusBySixId } from '@/store/selectors/connection';
import { selectCurrentUserId } from '@/store/selectors/auth';
import { resolveIsOwner } from '@/utils/permissions';
import type { ConnectionStatus } from '@/store/slices/poc/connectionSlice';
import { PENDING_HAP_STATUS, type SixCatalogItem } from '@/types/Sixes';
import type { SortKey, SortOption } from '@/types/SixCatalog.types';
import { SORT_KEY_TO_PARAMS } from '@/types/SixCatalog.types';

export type { SortKey, SortOption } from '@/types/SixCatalog.types';

export const SORT_OPTIONS: SortOption[] = [
  { key: 'lastActivity', label: 'Last Activity' },
  { key: 'name', label: 'Name' },
  { key: 'members', label: 'Members' },
  { key: 'files', label: 'Files' },

];

const SEARCH_DEBOUNCE_MS = 300;
const PENDING_HAP_POLL_MS = 15_000;

interface UseSixCatalogPageState {
  searchValue: string;
  selectedSortKey: SortKey;
  sortedSixes: SixCatalogItem[];
  showEmptyState: boolean;
  sortOptions: SortOption[];
  connectionStatusBySixId: Record<string, ConnectionStatus>;
  ownershipBySixId: Record<string, boolean>;
}

interface UseSixCatalogPageHandlers {
  handleSearchChange: (value: string) => void;
  handleSearchClear: () => void;
  handleSortChange: (nextValue: string) => void;
  handleCardClick: (sixId: string) => void;
  handleCreateNewSix: () => void;
  handleToggleFavorite: (sixId: string) => void;
  handleEdit: (sixId: string) => void;
  handleDelete: (sixId: string) => void;
}

interface UseSixCatalogPageReturn {
  state: UseSixCatalogPageState;
  handlers: UseSixCatalogPageHandlers;
}

export function useSixCatalogPage(): UseSixCatalogPageReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sixes = useAppSelector(selectCatalogSixes);
  const storeSearch = useAppSelector(selectCatalogSearch);
  const activeFilters = useAppSelector(selectCatalogFilters);
  const connectionStatusBySixId = useAppSelector(selectConnectionStatusBySixId);
  const currentUserId = useAppSelector(selectCurrentUserId);

  const [searchValue, setSearchValue] = useState(storeSearch ?? '');
  const [selectedSortKey, setSelectedSortKey] = useState<SortKey>('lastActivity');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Fetch catalog on every mount so file counts stay current
  useEffect(() => {
    void dispatch(fetchSixCatalogThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!sixes.some((six) => six.status === PENDING_HAP_STATUS)) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void dispatch(fetchSixCatalogThunk());
    }, PENDING_HAP_POLL_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, sixes]);

  // Debounced search — dispatch API call after user stops typing
  useEffect(() => {
    const trimmedSearch = searchValue.trim();
    const currentStoreSearch = storeSearch ?? '';

    if (trimmedSearch === currentStoreSearch) return;

    debounceTimerRef.current = setTimeout(() => {
      void dispatch(
        setSearchAndFetchThunk(trimmedSearch.length > 0 ? trimmedSearch : undefined),
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current !== undefined) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [dispatch, searchValue, storeSearch]);

  const sortedSixes = useClientCatalogFiltering({
    sixes,
    searchValue,
    activeFilters,
    currentUserId,
    selectedSortKey,
  });

  const showEmptyState = sortedSixes.length === 0;

  const ownershipBySixId = useMemo<Record<string, boolean>>(
    () => {
      const result: Record<string, boolean> = {};
      for (const six of sixes) {
        result[String(six.id)] = resolveIsOwner(currentUserId, six.owners);
      }
      return result;
    },
    [sixes, currentUserId],
  );

  const handleSearchChange = useCallback((value: string): void => {
    setSearchValue(value);
  }, []);

  const handleSearchClear = useCallback((): void => {
    setSearchValue('');
  }, []);

  const handleSortChange = useCallback(
    (nextValue: string): void => {
      const matchedOption = SORT_OPTIONS.find((opt) => opt.key === nextValue);
      if (matchedOption !== undefined) {
        setSelectedSortKey(matchedOption.key);
        const params = SORT_KEY_TO_PARAMS[matchedOption.key];
        void dispatch(setSortAndFetchThunk(params));
      }
    },
    [dispatch],
  );

  const handleCardClick = useCallback(
    (sixId: string): void => {
      void navigate({ to: `/app/sixes/${sixId}` });
    },
    [navigate],
  );

  const handleCreateNewSix = useCallback((): void => {
    dispatch(showCreateSixModal());
  }, [dispatch]);

  const handleToggleFavorite = useCallback(
    (sixId: string): void => {
      void dispatch(toggleFavoriteSixThunk(sixId))
        .unwrap()
        .catch(() => {
          showErrorToast('Failed to update favorite');
        });
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (sixId: string): void => {
      void dispatch(showEditSixModal(sixId));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (sixId: string): void => {
      void dispatch(deleteSixThunk(sixId))
        .unwrap()
        .then(() => {
          showSuccessToast('SIX deleted');
        })
        .catch(() => {
          showErrorToast('Failed to delete SIX');
        });
    },
    [dispatch],
  );

  return {
    state: {
      searchValue,
      selectedSortKey,
      sortedSixes,
      showEmptyState,
      sortOptions: SORT_OPTIONS,
      connectionStatusBySixId,
      ownershipBySixId,
    },
    handlers: {
      handleSearchChange,
      handleSearchClear,
      handleSortChange,
      handleCardClick,
      handleCreateNewSix,
      handleToggleFavorite,
      handleEdit,
      handleDelete,
    },
  };
}
